import _ from 'lodash'
import React from 'react'
import { addDays, differenceInBusinessDays, format, startOfDay } from 'date-fns'
import SessionStore from '../../State/SessionStore'
import {
    canViewCostCentres,
    canViewProjectInvoices,
    canViewRoles,
    canViewRoute,
    canViewStaffAllocations,
    canViewStaffChargeOutRate,
    canViewStaffCostRate,
    canViewStaffOvertimeRate,
    canViewStaffPayRate,
} from '../../State/Permissions/HasPermissions'
import { Link } from '@tanstack/react-router'
import PermissionCollection from '../../State/Collections/PermissionCollection'

export const StaffReportColumns = (report) => ({
    name: {
        id: 'name',
        label: 'Name',
        type: 'text',
        width: 25,
        value: (row) => row.fullName,
        component: ({ value, stores }) => {
            const { row } = stores
            return (
                <div>
                    <Link
                        to={`/staff/${row.rowObject.staffId || row.rowObject.id}`}
                    >
                        {value}
                    </Link>
                </div>
            )
        },
    },
    staff: {
        id: 'staff',
        label: 'Staff',
        type: 'staffMember',
        width: 25,
        visible: false,
        value: (row) => row.staff,
        format: (staff) => staff,
        component: ({ value, stores }) => {
            const { row } = stores
            return (
                <div>
                    <Link
                        to={`/staff/${row.rowObject.staffId || row.rowObject.id}`}
                    >
                        {value}
                    </Link>
                </div>
            )
        },
    },
    lastName: {
        id: 'lastName',
        label: 'Last name',
        type: 'text',
        width: 15,
        value: (row) => row.lastName,
        component: ({ value, stores }) => {
            const { row } = stores
            return (
                <div>
                    <Link
                        to={`/staff/${row.rowObject.staffId || row.rowObject.id}`}
                    >
                        {value}
                    </Link>
                </div>
            )
        },
    },
    firstName: {
        id: 'firstName',
        label: 'First name',
        type: 'text',
        width: 15,
        value: (row) => row.firstName,
        component: ({ value, stores }) => {
            const { row } = stores
            return (
                <div>
                    <Link
                        to={`/staff/${row.rowObject.staffId || row.rowObject.id}`}
                    >
                        {value}
                    </Link>
                </div>
            )
        },
    },
    email: {
        id: 'email',
        label: 'Email',
        type: 'text',
        width: 25,
        value: (row) => row.email,
        component: ({ value, stores }) => {
            const { row } = stores
            return (
                <div>
                    <Link
                        to={`/staff/${row.rowObject.staffId || row.rowObject.id}`}
                    >
                        {value}
                    </Link>
                </div>
            )
        },
    },
    role: {
        id: 'role',
        label: 'Role',
        type: 'role',
        width: 15,
        value: (row) => row.role,
        format: (role) => role,
        permissions: (row) => canViewRoles(SessionStore.user),
    },
    costCentre: {
        id: 'costCentre',
        label: 'Cost Centre',
        type: 'costCentre',
        width: 15,
        value: (row) => row.costCentre,
        format: (costCentre) => costCentre,
        permissions: (row) => canViewCostCentres(SessionStore.user),
    },
    permissionName: {
        id: 'permissionName',
        label: 'Permission Name',
        type: 'text',
        width: 20,
        value: (row) => {
            return PermissionCollection.permissionsById[row.permissionsId].name
        },
        permissions: (row) =>
            canViewRoute(SessionStore.user, `staff-permissions`),
    },
    hasLogin: {
        id: 'hasLogin',
        label: 'Has Login',
        type: 'boolean',
        width: 15,
        value: (row) => row.hasLogin,
    },
    isArchived: {
        id: 'isArchived',
        label: 'Is Archived',
        type: 'boolean',
        width: 15,
        value: (row) => row.isArchived,
    },
    isActive: {
        id: 'isActive',
        label: 'Is Active',
        type: 'boolean',
        width: 15,
        value: (row) => !row.isArchived,
    },
    payRate: {
        id: 'payRate',
        label: 'Pay Rate',
        type: 'number',
        width: 15,
        value: (row) => row.payRate,
        permissions: (row) => canViewStaffPayRate(SessionStore.user),
    },
    overtimeRate: {
        id: 'overtimeRate',
        label: 'Overtime Rate',
        type: 'number',
        width: 15,
        value: (row) => row.overtimeRate,
        permissions: (row) => canViewStaffOvertimeRate(SessionStore.user),
    },
    costRate: {
        id: 'costRate',
        label: 'Cost Rate',
        type: 'number',
        width: 15,
        value: (row) => row.costRate,
        permissions: (row) => canViewStaffCostRate(SessionStore.user),
    },
    chargeOutRate: {
        id: 'chargeOutRate',
        label: 'Charge Out Rate',
        type: 'number',
        width: 15,
        value: (row) => row.chargeOutRate,
        permissions: (row) => canViewStaffChargeOutRate(SessionStore.user),
    },
    weeklyAvailability: {
        id: 'weeklyAvailability',
        label: 'Weekly Availability',
        type: 'number',
        width: 15,
        value: (row) => row.availability / 60,
    },
    allocatedHours: {
        id: 'allocatedHours',
        label: 'Allocated hours',
        type: 'number',
        width: 15,
        value: (row) => row.allocatedHours,
        permissions: (row) => canViewStaffAllocations(SessionStore.user),
    },
    availableHours: {
        id: 'availableHours',
        label: 'Available hours',
        type: 'number',
        width: 15,
        value: (row) => row.availableHours,
    },
    remainingHours: {
        id: 'remainingHours',
        label: 'Remaining hours',
        type: 'number',
        width: 15,
        value: (row) => row.remainingHours,
    },
    allocatedChargeOut: {
        id: 'allocatedChargeOut',
        label: 'Allocated charge out',
        type: 'currency',
        width: 15,
        value: (row) => row.allocatedChargeOut,
        permissions: (row) => canViewStaffAllocations(SessionStore.user),
    },
    availableChargeOut: {
        id: 'availableChargeOut',
        label: 'Available charge out',
        type: 'currency',
        width: 15,
        value: (row) => row.availableChargeOut,
    },
    remainingChargeOut: {
        id: 'remainingChargeOut',
        label: 'Remaining charge out',
        type: 'currency',
        width: 15,
        value: (row) => row.remainingChargeOut,
    },
    recordedHours: {
        id: 'recordedHours',
        label: 'Recorded hours',
        type: 'number',
        width: 15,
        value: (row) => row.recordedHours,
    },
    billableHours: {
        id: 'billableHours',
        label: 'Billable Hours',
        type: 'number',
        width: 15,
        value: (row) => row.billableHours,
    },
    nonBillableHours: {
        id: 'nonBillableHours',
        label: 'Non Billable Hours',
        type: 'number',
        width: 15,
        value: (row) => row.nonBillableHours,
    },
    percentBillableHours: {
        id: 'percentBillableHours',
        label: 'Percent Billable Hours',
        type: 'percent',
        width: 15,
        value: (row) => row.percentBillableHours,
    },
    overtimeHours: {
        id: 'overtimeHours',
        label: 'Overtime hours',
        type: 'number',
        width: 15,
        value: (row) => row.overtimeHours,
    },
    invoicedHours: {
        id: 'invoicedHours',
        label: 'Invoiced Hours',
        type: 'number',
        width: 15,
        value: (row) => row.invoicedHours,
    },
    pay: {
        id: 'pay',
        label: 'Pay',
        type: 'currency',
        width: 15,
        value: (row) => row.pay,
        permissions: (row) => canViewStaffPayRate(SessionStore.user),
    },
    cost: {
        id: 'cost',
        label: 'Cost',
        type: 'currency',
        width: 15,
        value: (row) => row.cost,
        permissions: (row) => canViewStaffCostRate(SessionStore.user),
    },
    chargeOut: {
        id: 'chargeOut',
        label: 'Charge Out',
        type: 'currency',
        width: 15,
        value: (row) => row.chargeOut,
        permissions: (row) => canViewStaffChargeOutRate(SessionStore.user),
    },
    revenueEarned: {
        id: 'revenueEarned',
        label: 'Revenue Earned',
        type: 'currency',
        width: 15,
        value: (row) => row.revenueEarned,
        permissions: (row) =>
            canViewStaffChargeOutRate(SessionStore.user) &&
            canViewProjectInvoices(SessionStore.user),
    },
    revenueEarnedPerHour: {
        id: 'revenueEarnedPerHour',
        label: 'Revenue Earned Per Hour',
        type: 'currency',
        width: 15,
        value: (row) => row.revenueEarnedPerHour,
        permissions: (row) =>
            canViewStaffChargeOutRate(SessionStore.user) &&
            canViewProjectInvoices(SessionStore.user),
    },
    projects: {
        id: 'projects',
        label: 'Projects',
        type: 'project',
        width: 15,
        value: (row) => row.projects,
        format: (projects) => projects,
    },
})
